import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axiosInstance from '../Services/axiosInstance';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useToaster } from '../components/common/Toaster';
import { Loader2, User, Save, X, Mail } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardIconHeader,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';

const Profile: React.FC = () => {
  const auth = useAuth();
  const { notify } = useToaster();
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [originalDisplayName, setOriginalDisplayName] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {
        const res = await axiosInstance.get('/api/Profile', { signal: controller.signal });
        if (controller.signal.aborted) return;
        const name = res.data?.name || auth.user?.name || '';
        const emailValue = res.data?.email || auth.user?.email || '';
        setDisplayName(name);
        setOriginalDisplayName(name);
        setEmail(emailValue);
      } catch {
        if (controller.signal.aborted) return;
        const name = auth.user?.name || '';
        setDisplayName(name);
        setOriginalDisplayName(name);
        setEmail(auth.user?.email || '');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    load();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user?.email]);

  const isDirty = displayName !== originalDisplayName;

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await axiosInstance.put('/api/Profile', { name: displayName });
      setOriginalDisplayName(displayName);
      notify('Saved', 'Profile preferences updated', 'success');
    } catch (error) {
      notify('Error', 'Failed to save profile', 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setDisplayName(originalDisplayName);
  };

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  if (loading) return <LoadingSpinner label="Loading profile..." />;

  // Get initials for avatar
  const getInitials = (name: string) => {
    if (!name || name.trim().length === 0) return '??';
    const cleanName = name.trim();
    const parts = cleanName.split(/[@.\s]+/).filter(p => p.length > 0);
    if (parts.length >= 2 && parts[0].length > 0 && parts[1].length > 0) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    if (parts.length === 1 && parts[0].length >= 2) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return cleanName.slice(0, 2).toUpperCase();
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardIconHeader icon={<User size={22} className="text-primary" />}>
          <CardTitle>Profile</CardTitle>
          <CardDescription>
            Manage your personal information and preferences. Changes made here will be
            reflected across the application.
          </CardDescription>
        </CardIconHeader>
      </Card>

      {/* Profile Information */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Avatar Card */}
        <Card className="animate-fade-in-up">
          <CardHeader>
            <CardTitle className="text-lg">Avatar</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            <div className="w-24 h-24 rounded-full bg-primary/10 ring-2 ring-primary/20 ring-offset-2 ring-offset-background flex items-center justify-center mb-4 animate-scale-in">
              <span className="text-2xl font-semibold text-primary">
                {getInitials(displayName || email)}
              </span>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Your avatar is generated from your display name.
            </p>
          </CardContent>
        </Card>

        {/* Details Card */}
        <Card className="lg:col-span-2 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
          <CardHeader>
            <CardTitle className="text-lg">Personal Information</CardTitle>
            <CardDescription>
              Update your display name and view your account details.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="profile-display-name">Display name</Label>
                <Input
                  id="profile-display-name"
                  placeholder="Enter your display name"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  aria-describedby="displayNameHelp"
                />
                {isDirty && (
                  <Badge variant="warning">Unsaved changes</Badge>
                )}
                <p id="displayNameHelp" className="text-sm text-muted-foreground">
                  This name may be shown in activity and approvals.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="profile-email" className="flex items-center gap-1.5">
                  <Mail size={14} className="text-muted-foreground" />
                  Email
                </Label>
                <Input
                  id="profile-email"
                  value={email}
                  disabled
                  aria-describedby="emailHelp"
                  className="bg-muted"
                />
                <p id="emailHelp" className="text-sm text-muted-foreground">
                  Managed by your identity provider. Contact your administrator to change.
                </p>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  onClick={handleCancel}
                  variant="outline"
                  disabled={!isDirty || isLoading}
                >
                  <X className="mr-2 h-4 w-4" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!isDirty || isLoading}
                >
                  {isLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-2 h-4 w-4" />
                  )}
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Profile;
